import {
  FinalVerdict,
  IOCDataTypes,
  IOCFileTypes,
  IOCFinalGroup,
  IOCGroupedByDataType,
  IOCGroupedByFileType,
  IOCItem,
  IOCPrevalenceVerdicts,
  SignalVerdict,
} from '../../types'
import { ReportFilterValues } from '../../components/filter/types'
import { listToObj, objectHasOneOfKeys } from 'app/modules/shared/helpers'
import { cloneDeep, has } from 'lodash'

export function getEnumKeyByValue(enumSet: any, value: any) {
  return Object.keys(enumSet)
    .filter((key) => enumSet[key as keyof typeof enumSet] === value)
    .pop()
}

/**
 * Check if some items of given type have osint verdicts
 */
export function checkHasOsintVerdicts(data: IOCFinalGroup[]): boolean {
  for (const group of data) {
    if (group.hasOsintVerdicts) {
      return true
    }
  }

  return false
}

export function checkHasVerdicts(data: IOCFinalGroup[]): boolean {
  for (const group of data) {
    if (checkGroupHasVerdicts(group.references)) {
      return true
    }
  }

  return false
}

export function checkGroupHasVerdicts(refs: IOCItem[]): boolean {
  for (const ref of refs) {
    if (ref.metaData?.isWhitelisted) {
      return true
    }
  }

  return false
}

// Run verdicst updates for all IOCs
export function runVerdictsUpdater(
  ioc: IOCGroupedByFileType | undefined,
  groupVerdicts: { [key: string]: string },
  prevalenceVerdicts: IOCPrevalenceVerdicts | undefined,
  reportVerdict: FinalVerdict | undefined
) {
  const iocsData = cloneDeep(ioc) || {}
  const hasUpdates: boolean[] = []

  // Update IOCs verdicts based on report verdict, prevalence etc.
  for (const fileType in iocsData) {
    const filesData = iocsData[fileType as keyof typeof iocsData]

    if (fileType === IOCFileTypes.Main) {
      const dataByType = filesData as IOCGroupedByDataType
      hasUpdates.push(updateIOCsVerdicts(dataByType, groupVerdicts, prevalenceVerdicts, reportVerdict))
      continue
    }

    for (const fileHash in filesData) {
      const dataByType = filesData[fileHash as keyof typeof filesData] as IOCGroupedByDataType
      hasUpdates.push(updateIOCsVerdicts(dataByType, groupVerdicts, prevalenceVerdicts, reportVerdict))
    }
  }

  return [iocsData, hasUpdates.some((item) => item)] as [IOCGroupedByFileType, boolean]
}

// Update verdicts of IOCs based on:
// - prevalence
// - report verdict
// - group verdict
// - osint verdicts
export function updateIOCsVerdicts(
  data: IOCGroupedByDataType,
  groupVerdicts: { [key: string]: string },
  prevalenceVerdicts: IOCPrevalenceVerdicts | undefined,
  reportVerdict: FinalVerdict | undefined
): boolean {
  let hasUpdates = false

  Object.values(data).forEach((groups) => {
    for (const group of groups) {
      for (const item of group.references) {
        let verdict = SignalVerdict.Unknown

        if (item.osintVerdicts?.[0]?.verdict?.code === SignalVerdict.Malicious) {
          verdict = SignalVerdict.Malicious
        } else if (item.metaData?.isWhitelisted) {
          verdict = SignalVerdict.Benign
        } else if (item.metaData?.isBlacklisted) {
          verdict = SignalVerdict.Malicious
        } else {
          if (item.metaData?.triggeredConsumerIDs && groupVerdicts) {
            const groupIds = item.metaData?.triggeredConsumerIDs
            if (Array.isArray(groupIds) && groupIds.length > 0 && groupVerdicts[groupIds[0]]) {
              verdict = groupVerdicts[groupIds[0]] as SignalVerdict
            }
          }

          const prevalenceVerdict = prevalenceVerdicts?.[item.data]?.verdict
          if (prevalenceVerdict && prevalenceVerdict !== item.verdict) {
            item.verdict = prevalenceVerdict
            hasUpdates = true
          }

          if (item.verdict) {
            let preVerdict = item.verdict
            if (item.verdict === SignalVerdict.Unknown) {
              preVerdict = (reportVerdict ?? SignalVerdict.Unknown) as SignalVerdict
            }

            const malVerdicts = [SignalVerdict.Malicious, SignalVerdict.Likely_malicious]
            if (verdict === SignalVerdict.Unknown) {
              if (malVerdicts.includes(preVerdict) && item.interesting) {
                verdict = SignalVerdict.Suspicious
              }
            } else if (verdict === SignalVerdict.Suspicious) {
              const isMalicious = malVerdicts.includes(item.verdict)
              if (isMalicious) {
                verdict = item.verdict
              }
            }
          }
        }

        if (verdict !== item.verdict && (!item.verdict || item.verdict === SignalVerdict.Unknown)) {
          item.verdict = verdict
          hasUpdates = true
        }
      }
    }
  })

  return hasUpdates
}

export function applyFilter(data: any, filterValues: ReportFilterValues | undefined) {
  if (!Object.keys(filterValues || {})?.length) {
    return data
  }

  const result: { [key: string]: any } = {}
  const filter = filterValues as ReportFilterValues
  const typeFilter = listToObj(filter.type)
  const originFilter = listToObj(filter.origin)
  const verdictFilter = listToObj(filter.verdict)
  const query = filter.search?.length ? filter.search[0].toLowerCase() : ''

  for (const fileType in data) {
    if (filter.type?.length && typeof typeFilter[fileType] === 'undefined') {
      continue
    }

    const iocTypeData = data[fileType]
    const groups = []
    for (const originGroup of iocTypeData) {
      if (filter.origin?.length && !objectHasOneOfKeys(originFilter, originGroup.origins)) {
        continue
      }

      const group = { ...originGroup, references: [] }

      for (const item of originGroup.references) {
        const skip =
          (filter.verdict?.length && (!item.verdict || typeof verdictFilter[item.verdict] === 'undefined')) ||
          (query && item.data.toLowerCase().indexOf(query) === -1)

        if (!skip) {
          group.references.push(item)
        }
      }

      if (group.references.length) {
        groups.push(group)
      }
    }

    if (groups.length) {
      result[fileType] = groups
    }
  }

  return result
}

// Helper to apply some function to every individual IOC item
export function iterateIocItems(
  iocs: IOCGroupedByFileType,
  callback: (item: IOCItem, itemType: IOCDataTypes) => void
): void {
  for (const fileType in iocs) {
    const filesData: IOCGroupedByDataType = iocs[fileType as keyof typeof iocs]

    if (fileType === IOCFileTypes.Main) {
      iterateByIocType(filesData, callback)
      continue
    }

    for (const fileHash in filesData) {
      const fileIoc = filesData[fileHash as keyof typeof filesData] as IOCGroupedByDataType
      iterateByIocType(fileIoc, callback)
    }
  }

  // Nested iteration function
  function iterateByIocType(
    iocsByType: IOCGroupedByDataType,
    callback: (item: IOCItem, itemType: IOCDataTypes) => void
  ): void {
    for (const iocType in iocsByType) {
      const groups = iocsByType[iocType as keyof typeof iocsByType] as IOCFinalGroup[]
      for (const group of groups) {
        for (const item of group.references) {
          callback(item, iocType as IOCDataTypes)
        }
      }
    }
  }
}
